import React, { useState, useEffect } from 'react';
import { DonationService } from '../../services/DonationService';
import { ProjectService } from '../../services/ProjectService';
import { useOrganization } from '../../contexts/OrganizationContext';
import * as WechatAuthService from '../../services/WechatAuthService';
import { showError, logError, logInfo } from '../../utils/errorHandler';
import { formatRelativeTime } from '../../utils/dateFormat';
import { Donation } from '../../shared/models/Donation';
import { Project } from '../../shared/models/Project';
import { TopBar } from '../../components/TopBar/TopBar';
import { DonationStatsBar } from '../../components/DonationStatsBar/DonationStatsBar';
import './MyDonationsView.css';

interface MyDonationsViewProps {
  onBack: () => void;
  onDonationClick: (donation: Donation) => void;
  projectId?: string; // 可选：如果提供，只显示该项目的捐赠记录
}

/**
 * 我的捐赠列表页面
 */
export const MyDonationsView: React.FC<MyDonationsViewProps> = ({ onBack, onDonationClick, projectId }) => {
  const { organization } = useOrganization();
  const [donations, setDonations] = useState<Donation[]>([]);
  const [projects, setProjects] = useState<Map<string, Project>>(new Map());
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalAmount, setTotalAmount] = useState(0);
  const [projectCount, setProjectCount] = useState(0);

  useEffect(() => {
    loadDonations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDonations = async (pageNum: number = 1, append: boolean = false) => {
    try {
      const openId = WechatAuthService.getOpenId();
      if (!openId) {
        showError('未获取到微信授权信息', 'MyDonationsView.loadDonations');
        setLoading(false);
        return;
      }

      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params: any = {
        donorOpenId: openId,
        page: pageNum,
        limit: 20,
      };
      // 根据机构类型决定使用哪个过滤参数
      if (organization?.type === 'PUBLIC') {
        params.organizationId = organization.id;
      } else if (organization?.type === 'EXECUTING') {
        params.executingOrganizationId = organization.id;
      }
      if (projectId) {
        params.projectId = projectId; // 如果提供了 projectId，只获取该项目的捐赠记录
      }
      const response = await DonationService.getDonations(params);

      // 只显示已支付的订单
      const paidDonations = response.list.filter(d => d.status === 'PAID');

      if (append) {
        setDonations(prev => [...prev, ...paidDonations]);
      } else {
        setDonations(paidDonations);
        // 计算统计信息（只在第一页时计算）
        calculateStats(paidDonations);
      }

      // 加载项目信息
      const projectIds = Array.from(new Set(paidDonations.map(d => d.projectId)));
      await loadProjects(projectIds);

      setPage(response.page);
      setHasMore(response.page < response.totalPages);

      // 如果是第一页，需要计算所有页面的统计信息
      if (pageNum === 1 && response.totalPages > 1) {
        calculateAllStats(openId, response.totalPages);
      }
    } catch (error) {
      logError('[MyDonationsView] Failed to load donations', error);
      showError('加载捐赠记录失败', 'MyDonationsView.loadDonations');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadProjects = async (projectIds: string[]) => {
    try {
      const projectPromises = projectIds.map(id => ProjectService.getProjectById(id));
      const projectList = await Promise.all(projectPromises);
      const projectMap = new Map<string, Project>();
      projectList.forEach(project => {
        if (project) {
          projectMap.set(project.id, project);
        }
      });
      setProjects(prev => {
        const merged = new Map<string, Project>();
        // 合并旧的 Map
        prev.forEach((value, key) => {
          merged.set(key, value);
        });
        // 合并新的 Map
        projectMap.forEach((value, key) => {
          merged.set(key, value);
        });
        return merged;
      });
    } catch (error) {
      logError('[MyDonationsView] Failed to load projects', error);
      // 不显示错误，项目信息加载失败不影响列表显示
    }
  };

  const calculateStats = (donationList: Donation[]) => {
    let amount = 0;
    const projectIds = new Set<string>();

    donationList.forEach(donation => {
      if (donation.donationType === 'MONTHLY' && donation.totalDonationAmount) {
        amount += donation.totalDonationAmount;
      } else {
        amount += donation.amount;
      }
      if (donation.projectId) {
        projectIds.add(donation.projectId);
      }
    });

    setTotalAmount(amount);
    setProjectCount(projectIds.size);
  };

  const calculateAllStats = async (openId: string, totalPages: number) => {
    try {
      let totalAmount = 0;
      const projectIds = new Set<string>();
      let currentPage = 1;

      while (currentPage <= totalPages && currentPage <= 100) {
        const params: any = {
          donorOpenId: openId,
          page: currentPage,
          limit: 100,
        };
        // 根据机构类型决定使用哪个过滤参数
        if (organization?.type === 'PUBLIC') {
          params.organizationId = organization.id;
        } else if (organization?.type === 'EXECUTING') {
          params.executingOrganizationId = organization.id;
        }
        const response = await DonationService.getDonations(params);

        response.list.forEach(donation => {
          if (donation.status === 'PAID') {
            if (donation.donationType === 'MONTHLY' && donation.totalDonationAmount) {
              totalAmount += donation.totalDonationAmount;
            } else {
              totalAmount += donation.amount;
            }
            if (donation.projectId) {
              projectIds.add(donation.projectId);
            }
          }
        });

        currentPage++;
      }

      setTotalAmount(totalAmount);
      setProjectCount(projectIds.size);
    } catch (error) {
      logError('[MyDonationsView] Failed to calculate all stats', error);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      loadDonations(page + 1, true);
    }
  };

  if (loading) {
    return (
      <div className="my-donations-view">
        <TopBar title="捐赠记录" showBack onBack={onBack} />
        <div className="my-donations-loading">加载中...</div>
      </div>
    );
  }

  return (
    <div className="my-donations-view">
      <TopBar title="捐赠记录" showBack onBack={onBack} />
      
      <div className="my-donations-content">
        {/* 统计卡片 */}
        <div className="my-donations-stats">
          <DonationStatsBar
            totalAmount={totalAmount}
            projectCount={projectCount}
          />
        </div>

        {/* 捐赠列表 */}
        {donations.length === 0 ? (
          <div className="my-donations-empty">暂无捐赠记录</div>
        ) : (
          <div className="my-donations-list">
            {donations.map((donation) => {
              const project = projects.get(donation.projectId);
              const projectName = project?.title || '未知项目';
              const displayAmount = donation.donationType === 'MONTHLY' && donation.totalDonationAmount
                ? donation.totalDonationAmount
                : donation.amount;
              const paymentTime = donation.paymentTime || donation.createTime;

              return (
                <div
                  key={donation.id}
                  className="my-donations-item"
                  onClick={() => onDonationClick(donation)}
                >
                  <div className="my-donations-item-content">
                    <div className="my-donations-item-title">{projectName}</div>
                    <div className="my-donations-item-time">{formatRelativeTime(paymentTime)}</div>
                  </div>
                  <div className="my-donations-item-amount">¥{displayAmount.toFixed(2)}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* 加载更多 */}
        {hasMore && donations.length > 0 && (
          <div className="my-donations-load-more">
            <button
              className="my-donations-load-more-btn"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? '加载中...' : '加载更多'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

